import React, { useEffect, useMemo, useRef, useState } from "react";
import { Empty, Input, Spin, Tag } from "antd";
import { useHistory } from "react-router-dom";
import { RiCommandLine, RiMapPinLine, RiShieldLine, RiUser3Line } from "react-icons/ri";
import ApiService from "../../../network/ApiService";
import adminNavigation from "../../../navigation/adminNavigation";

const typeLabels = {
  player: "Spieler",
  referee: "Schiedsrichter",
  team: "Mannschaft",
  league: "Liga",
  season: "Saison",
  stadium: "Stadion",
  user: "Benutzer",
};

const pageUrls = {
  Administration: "/admin",
  "Admin-Benutzer": "/admin/user",
  "Team-Logins": "/admin/team/users",
  "Spieler-Berechtigungen": "/admin/team/pending",
  Mannschaften: "/admin/team",
  Spieler: "/admin/players",
  Schiedsrichter: "/admin/referee",
  Ligen: "/admin/league",
  Stadien: "/admin/stadiums",
  Registrierungen: "/admin/registrations",
  Strafsenat: "/admin/strafsenat",
  News: "/admin/news",
  "Strafsenat-News": "/admin/news_strafsenat",
  Veranstaltungen: "/admin/people-events",
  Rechnungen: "/admin/rechnungen",
  "Gebühren & Beträge": "/admin/fees",
  "Rechnung erstellen": "/admin/rechnung_erstellung",
  Transfers: "/admin/transfers",
  "Transfer-Einstellungen": "/admin/transfer_settings",
  Spielverlegungen: "/admin/postpone",
  Posteingang: "/admin/chat",
  "Push-Mitteilungen": "/admin/push-notifications",
  Rechtliches: "/admin/legal",
  Sponsoren: "/admin/sponsors",
};

const pageKeywords = {
  Posteingang: "chat nachrichten inbox",
  Mannschaften: "teams vereine clubs",
  Spieler: "players",
  Ligen: "leagues seasons saisonen",
  Stadien: "stadiums venues spielstätten",
};

const searchablePages = adminNavigation
  .filter(({ title }) => pageUrls[title])
  .map(({ title }) => ({ id: `page-${title}`, type: "page", title, subtitle: "Admin-Seite", url: pageUrls[title], keywords: pageKeywords[title] || "" }));

const fallbackIcons = {
  player: RiUser3Line,
  referee: RiUser3Line,
  team: RiShieldLine,
  stadium: RiMapPinLine,
};

function SearchResultVisual({ item }) {
  const [imageFailed, setImageFailed] = useState(false);
  const FallbackIcon = fallbackIcons[item.type];

  if (item.image && !imageFailed) {
    return (
      <span className={`oekfb-admin-search-results__visual${item.type === "team" ? " oekfb-admin-search-results__visual--contain" : ""}`}>
        <img src={item.image} alt="" onError={() => setImageFailed(true)} />
      </span>
    );
  }

  if (!FallbackIcon) return null;

  return (
    <span className="oekfb-admin-search-results__visual oekfb-admin-search-results__visual--fallback" aria-hidden="true">
      <FallbackIcon size={18} />
    </span>
  );
}

export default function HeaderSearch({ inputFocusProp, setSearchHeader }) {
  const history = useHistory();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const requestNumber = useRef(0);

  useEffect(() => {
    const openSearch = (event) => {
      if (!event.metaKey || event.key.toLowerCase() !== "k") return;

      event.preventDefault();
      setSearchHeader(true);
      inputFocusProp?.ref?.current?.focus();
    };

    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, [inputFocusProp?.ref, setSearchHeader]);

  const pageResults = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("de-AT");
    if (!normalized) return [];
    return searchablePages.filter((item) => `${item.title} ${item.keywords}`.toLocaleLowerCase("de-AT").includes(normalized)).slice(0, 4);
  }, [query]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }

    const currentRequest = ++requestNumber.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await new ApiService().get(`admin/search?query=${encodeURIComponent(term)}&limit=6`);
        if (currentRequest === requestNumber.current) setResults((response.items || []).slice(0, 10));
      } catch (error) {
        if (currentRequest === requestNumber.current) setResults([]);
      } finally {
        if (currentRequest === requestNumber.current) setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [query]);

  const allResults = [...pageResults, ...results];
  const selectResult = (item) => {
    setQuery("");
    setSearchHeader(false);
    history.push(item.url, { navigationTitle: item.title });
  };

  return (
    <div className="hp-position-relative">
      <Input
        {...inputFocusProp}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && allResults[0]) selectResult(allResults[0]);
          if (event.key === "Escape") {
            setQuery("");
            setSearchHeader(false);
          }
        }}
        autoComplete="off"
        aria-label="Administration durchsuchen"
        aria-keyshortcuts="Meta+K"
        placeholder="Spieler, Teams, Schiedsrichter, Ligen …"
        prefix={<i className="ri-search-line hp-text-color-black-60" />}
        suffix={(
          <span className="oekfb-admin-search-shortcut" aria-hidden="true">
            <RiCommandLine />
            <span>K</span>
          </span>
        )}
      />

      {query.trim() && (
        <div className="hp-header-search-result oekfb-admin-search-results">
          {loading && <div className="hp-p-16 hp-text-center"><Spin size="small" /></div>}
          {!loading && allResults.length === 0 && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={query.trim().length < 2 ? "Mindestens 2 Zeichen eingeben" : "Keine Treffer"} />
          )}
          {!loading && allResults.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {allResults.map((item) => (
                <li key={`${item.type}-${item.id}`}>
                  <button
                    type="button"
                    onClick={() => selectResult(item)}
                    className="oekfb-admin-search-results__item"
                  >
                    <SearchResultVisual item={item} />
                    <div className="oekfb-admin-search-results__content">
                      <div className="hp-d-flex hp-align-items-center" style={{ gap: 8 }}>
                        <Tag color={item.type === "page" ? "default" : "blue"}>{typeLabels[item.type] || "Seite"}</Tag>
                        <strong>{item.title}</strong>
                      </div>
                      {item.subtitle && <div className="hp-mt-4 hp-text-color-black-60">{item.subtitle}</div>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
